import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setDataLayout } from "../../data/redux/themeSettingSlice";
import ImageWithBasePath from "../imageWithBasePath";
import { logout, setUser } from "../../../core/data/redux/userSlice";
import socketService from "../../../services/socketService";
import { persistor } from "../../../core/data/redux/store";

import {
  setMobileSidebar,
  toggleMiniSidebar,
} from "../../data/redux/sidebarSlice";
import { all_routes } from "../../../feature-module/router/all_routes";
import { HorizontalSidebarData } from "../../data/json/horizontalSidebar";
import ChangePassword from "../../../feature-module/crm/settings/change-password";
import { showBasicAlert } from "../useAlert";
import api from "../../../services/api";
import axios from "axios";
import Loader from "../../../core/common/loader";

const Header = () => {
  const horizontalSidebarData = HorizontalSidebarData();
  const location = useLocation();
  const navigate = useNavigate();

  let targetRoute = "/";
  const routes = all_routes;
  const dispatch = useDispatch();

  const dataLayout = useSelector((state: any) => state.themeSetting.dataLayout);
  const user = useSelector((state: any) => state.user.user);

  if (user?.userDetails.role_char.toLowerCase() === "super admin") {
    targetRoute = routes.superAdminDashboard;
  } else if (user?.userDetails.role_char.toLowerCase() === "admin") {
    targetRoute = routes.adminDashboard;
  }



  const handleLogout = async () => {
    try {
      // 1. Set logout event BEFORE clearing localStorage
      localStorage.setItem("logout-event", Date.now().toString());

      // 2. Disconnect socket
      socketService.disconnect();

      // 3. Dispatch logout to Redux
      dispatch(logout());

      // 4. Purge persist
      await persistor.purge();
      await persistor.flush();

      // 5. NOW clear storage (after setting the event)
      localStorage.clear();
      sessionStorage.clear();

      // 6. Hard redirect
      window.location.href = routes.login;
    } catch (error) {
      console.error("Logout error:", error);
      window.location.href = routes.login;
    }
  };

  // Fixed storage listener
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "logout-event") {
        console.log("Logout detected from another tab");
        dispatch(logout());
        persistor.purge().then(() => {
          window.location.href = routes.login;
        });
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [routes.login, dispatch]);


  const Location = useLocation();
  const signature = localStorage.getItem("signature");

  const [subOpen, setSubopen] = useState<any>("");
  const [subsidebar, setSubsidebar] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [hover, setHover] = useState(false);
  const [userImage, setUserImage] = useState(
    user?.userDetails.profile_pic || ""
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const toggleSidebar = (title: any) => {
    localStorage.setItem("menuOpened", title);
    if (title === subOpen) {
      setSubopen("");
    } else {
      setSubopen(title);
    }
  };

  // Notification sections
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState<any>(0);
  const [notificationDropdownOpen, setNotificationDropdownOpen] =
    useState(false);
  const [currentUserId, setCurrentUserId] = useState<any>();

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      setCurrentUserId(userId);
      socketService.initialize(userId);

      const unsubscribe = socketService.onUpdate(
        ({
          notifications,
          unreadCount,
        }: {
          notifications: any[];
          unreadCount: number;
        }) => {
          setNotifications(notifications);
          setUnreadCount(unreadCount);
        }
      );

      return () => {
        unsubscribe();
        socketService.disconnect();
      };
    }
  }, []);

  const handleMarkAsRead = (notificationId: any) => {
    socketService.markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    socketService.markAllAsRead();
  };

  // Format time display
  const formatTime = (dateString: any) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // End Notification sections

  const toggleSubsidebar = (subitem: any) => {
    if (subitem === subsidebar) {
      setSubsidebar("");
    } else {
      setSubsidebar(subitem);
    }
  };
  const mobileSidebar = useSelector(
    (state: any) => state.sidebarSlice.mobileSidebar
  );

  const toggleMobileSidebar = () => {
    dispatch(setMobileSidebar(!mobileSidebar));
  };

  const handleToggleMiniSidebar = () => {
    if (dataLayout === "mini_layout") {
      dispatch(setDataLayout("default_layout"));
      localStorage.setItem("dataLayout", "default_layout");
    } else {
      dispatch(toggleMiniSidebar());
    }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((err) => { });
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => { });
        }
        setIsFullscreen(false);
      }
    }
  };
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsLoading(true);
    const file = event.target.files?.[0];
    if (!file) return;

    const cloudRes = await uploadFileToCloudinary(file);
    if (cloudRes?.secure_url) {
      const imageUrl = cloudRes.secure_url;

      try {
        const updateRes = await api.post("/userService/updateProfilePicture", {
          signature,
          profilePicUrl: imageUrl,
        });

        const success = updateRes.data?.message?.[0]?.status;

        if (success) {
          showBasicAlert("Avatar updated successfully!");
          dispatch(
            setUser({
              ...user,
              userDetails: { ...user.userDetails, profile_pic: imageUrl },
            })
          );
          setUserImage(imageUrl);
          setIsLoading(false);
        }
      } catch (err) {
        setIsLoading(false);
        console.error("Failed to save avatar:", err);
        showBasicAlert("Failed to update avatar.");
      }
    }
  };

  const uploadFileToCloudinary = async (file: File) => {
    const UPLOAD_PRESET: any = process.env.REACT_APP_UPLOAD_PRESET;
    const CLOUDINARY_URL = `${process.env.REACT_APP_API_CLOUDNIRY_URL}/${process.env.REACT_APP_CLOUD_NAME}/upload`;
    const FOLDER_PATH = "cms/profile_pic/" + user?.userDetails.id;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    formData.append("folder", FOLDER_PATH);

    try {
      const response = await axios.post(CLOUDINARY_URL, formData);
      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      showBasicAlert("Failed to upload file. Please try again.");
      return null;
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      {/* Header */}
      <div className="header">
        <div className="main-header">
          <div className="header-left">
            <Link to={targetRoute} className="logo logo-normal">
              <ImageWithBasePath
                src="assets/img/logo.svg"
                alt="Logo"
                width={70}
                height={50}
              />
            </Link>
            <Link to={targetRoute} className="dark-logo">
              <ImageWithBasePath
                src="assets/img/logo.svg"
                alt="Logo"
                width={70}
                height={50}
              />
            </Link>
          </div>

          <Link
            id="mobile_btn"
            onClick={(e: any) => {
              e.preventDefault();
              toggleMobileSidebar();
            }}
            className="mobile_btn"
            to="#sidebar"
          >
            <span className="bar-icon">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </Link>

          <div className="header-user">
            <div className="nav user-menu nav-list">
              <div
                className="me-auto d-flex align-items-center"
                id="header-search"
              >
                <Link
                  id="toggle_btn"
                  to="#"
                  onClick={(e: any) => {
                    e.preventDefault();
                    handleToggleMiniSidebar();
                  }}
                  className="btn btn-menubar me-1"
                >
                  <i className="ti ti-arrow-bar-to-left"></i>
                </Link>

                {user?.userDetails.role_char.toLowerCase() != "super admin" &&
                  user?.userDetails.role_char.toLowerCase() != "admin" && (
                    <div className="dropdown crm-dropdown">
                      <Link
                        to="#"
                        className="btn btn-menubar me-1"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-layout-grid"></i>
                      </Link>
                      <div className="dropdown-menu dropdown-lg dropdown-menu-start">
                        <div className="card mb-0 border-0 shadow-none">
                          <div className="card-header">
                            <h4>CRM</h4>
                          </div>
                          <div className="card-body pb-1">
                            <div className="row">
                              <div className="col-sm-6">
                                {/* <Link
                                to={routes.contactList}
                                className="d-flex align-items-center justify-content-between p-2 crm-link mb-3"
                              >
                                <span className="d-flex align-items-center me-3">
                                  <i className="ti ti-user-shield text-default me-2"></i>
                                  Contacts
                                </span>
                                <i className="ti ti-arrow-right"></i>
                              </Link> */}
                                <Link
                                  to={routes.dealsGrid}
                                  className="d-flex align-items-center justify-content-between p-2 crm-link mb-3"
                                >
                                  <span className="d-flex align-items-center me-3">
                                    <i className="ti ti-heart-handshake text-default me-2"></i>
                                    Deals
                                  </span>
                                  <i className="ti ti-arrow-right"></i>
                                </Link>
                                <Link
                                  to={routes.pipeline}
                                  className="d-flex align-items-center justify-content-between p-2 crm-link mb-3"
                                >
                                  <span className="d-flex align-items-center me-3">
                                    <i className="ti ti-timeline-event-text text-default me-2"></i>
                                    Pipeline
                                  </span>
                                  <i className="ti ti-arrow-right"></i>
                                </Link>
                              </div>
                              <div className="col-sm-6">
                                <Link
                                  to={routes.companiesGrid}
                                  className="d-flex align-items-center justify-content-between p-2 crm-link mb-3"
                                >
                                  <span className="d-flex align-items-center me-3">
                                    <i className="ti ti-building text-default me-2"></i>
                                    Companies
                                  </span>
                                  <i className="ti ti-arrow-right"></i>
                                </Link>
                                <Link
                                  to={routes.leadsGrid}
                                  className="d-flex align-items-center justify-content-between p-2 crm-link mb-3"
                                >
                                  <span className="d-flex align-items-center me-3">
                                    <i className="ti ti-user-check text-default me-2"></i>
                                    Leads
                                  </span>
                                  <i className="ti ti-arrow-right"></i>
                                </Link>
                                {/* <Link
                                to={routes.activity}
                                className="d-flex align-items-center justify-content-between p-2 crm-link mb-3"
                              >
                                <span className="d-flex align-items-center me-3">
                                  <i className="ti ti-activity text-default me-2"></i>
                                  Activities
                                </span>
                                <i className="ti ti-arrow-right"></i>
                              </Link> */}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* <Link to={routes.profilesettings} className="btn btn-menubar">
                  <i className="ti ti-settings-cog"></i>
                </Link> */}
              </div>

              <div
                className="sidebar sidebar-horizontal"
                id="horizontal-single"
              >
                <div className="sidebar-menu">
                  <div className="main-menu">
                    <ul className="nav-menu">
                      <li className="menu-title">
                        <span>Main</span>
                      </li>
                      {horizontalSidebarData?.map((mainMenu, index) => (
                        <React.Fragment key={`main-${index}`}>
                          {mainMenu?.menu?.map((data, i) => {
                            if (
                              user?.userDetails.menu.includes(data.menuValue)
                            ) {
                              return (
                                <li className="submenu" key={`menu-${i}`}>
                                  <Link
                                    to="#"
                                    className={`
                            ${data?.subMenus
                                        ?.map((link: any) => link?.route)
                                        .includes(Location.pathname)
                                        ? "active"
                                        : ""
                                      } ${subOpen === data.menuValue ? "subdrop" : ""}`}
                                    onClick={() =>
                                      toggleSidebar(data.menuValue)
                                    }
                                  >
                                    <i className={`ti ti-${data.icon}`}></i>
                                    <span>{data.menuValue}</span>
                                    <span className="menu-arrow"></span>
                                  </Link>

                                  {/* First-level Submenus */}
                                  <ul
                                    style={{
                                      display:
                                        subOpen === data.menuValue
                                          ? "block"
                                          : "none",
                                    }}
                                  >
                                    {data?.subMenus?.map((subMenu: any, j) => {
                                      if (
                                        user?.userDetails.menu_items.includes(
                                          subMenu.menuValue
                                        )
                                      ) {
                                        return (
                                          <li
                                            key={`submenu-${j}`}
                                            className={
                                              subMenu?.customSubmenuTwo
                                                ? "submenu"
                                                : ""
                                            }
                                          >
                                            <Link
                                              to={subMenu?.route || "#"}
                                              className={`${subMenu?.subMenusTwo
                                                ?.map(
                                                  (link: any) => link?.route
                                                )
                                                .includes(
                                                  Location.pathname
                                                ) ||
                                                subMenu?.route ===
                                                Location.pathname
                                                ? "active"
                                                : ""
                                                } ${subsidebar === subMenu.menuValue
                                                  ? "subdrop"
                                                  : ""
                                                }`}
                                              onClick={() =>
                                                toggleSubsidebar(
                                                  subMenu.menuValue
                                                )
                                              }
                                            >
                                              <span>{subMenu?.menuValue}</span>
                                              {subMenu?.customSubmenuTwo && (
                                                <span className="menu-arrow"></span>
                                              )}
                                            </Link>

                                            {/* Check if `customSubmenuTwo` exists */}
                                            {subMenu?.customSubmenuTwo &&
                                              subMenu?.subMenusTwo && (
                                                <ul
                                                  style={{
                                                    display:
                                                      subsidebar ===
                                                        subMenu.menuValue
                                                        ? "block"
                                                        : "none",
                                                  }}
                                                >
                                                  {subMenu.subMenusTwo.map(
                                                    (
                                                      subMenuTwo: any,
                                                      k: number
                                                    ) => (
                                                      <li
                                                        key={`submenu-two-${k}`}
                                                      >
                                                        <Link
                                                          className={
                                                            subMenuTwo.route ===
                                                              Location.pathname
                                                              ? "active"
                                                              : ""
                                                          }
                                                          to={subMenuTwo.route}
                                                        >
                                                          {subMenuTwo.menuValue}
                                                        </Link>
                                                      </li>
                                                    )
                                                  )}
                                                </ul>
                                              )}
                                          </li>
                                        );
                                      }
                                    })}
                                  </ul>
                                </li>
                              );
                            }
                          })}
                        </React.Fragment>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center">
                <div className="me-1">
                  <Link
                    to="#"
                    className="btn btn-menubar btnFullscreen"
                    onClick={(e: any) => {
                      e.preventDefault();
                      toggleFullscreen();
                    }}
                  >
                    <i className="ti ti-maximize"></i>
                  </Link>
                </div>
                <div className="me-1 notification_item">
                  <Link
                    to="#"
                    className="btn btn-menubar position-relative me-1"
                    id="notification_popup"
                    data-bs-toggle="dropdown"
                    onClick={(e: any) => {
                      e.preventDefault();
                      setNotificationDropdownOpen(!notificationDropdownOpen);
                    }}
                  >
                    <i className="ti ti-bell"></i>
                    {unreadCount > 0 && (
                      <span className="notification-status-dot"></span>
                    )}
                  </Link>
                  <div
                    className={`dropdown-menu dropdown-menu-end notification-dropdown p-4 ${notificationDropdownOpen ? "show" : ""
                      }`}
                    style={{
                      width: "350px",
                      maxHeight: "500px",
                      overflowY: "auto",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between border-bottom p-0 pb-3 mb-3">
                      <h4 className="notification-title">
                        Notifications ({notifications.length})
                      </h4>
                      <div className="d-flex align-items-center">
                        <Link
                          to="#"
                          className="text-primary fs-15 me-3 lh-1"
                          onClick={(e) => {
                            e.preventDefault();
                            handleMarkAllAsRead();
                          }}
                        >
                          Mark all as read
                        </Link>
                      </div>
                    </div>
                    <div className="noti-content">
                      <div className="d-flex flex-column">
                        {notifications.length > 0 ? (
                          notifications.map((notif) => (
                            <div
                              key={notif.id}
                              className={`border-bottom mb-3 pb-3 ${!notif.is_read ? "unread-notification" : ""
                                }`}
                            >
                              <Link
                                to={notif.todo_id ? `/application/todo/` : "#"}
                                onClick={(e: any) => {
                                  // Only prevent default if there's no todo_id (href is "#")
                                  if (!notif.todo_id) {
                                    e.preventDefault();
                                  }
                                  handleMarkAsRead(notif.id);
                                }}
                              >
                                <div className="d-flex">
                                  {/* <span className="avatar avatar-lg me-2 flex-shrink-0">
                                    <ImageWithBasePath
                                      src="assets/img/profiles/avatar-default.jpg"
                                      alt="Profile"
                                    />
                                  </span> */}
                                  <div className="flex-grow-1">
                                    <p className="mb-1">
                                      <span className="text-dark fw-semibold">
                                        {notif.message}
                                      </span>
                                    </p>
                                    <span>{formatTime(notif.createdAt)}</span>
                                    {!notif.is_read && (
                                      <span className="badge bg-primary ms-2">
                                        New
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </Link>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-3 text-muted">
                            No notifications available
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex p-0">
                      <Link
                        to="#"
                        className="btn btn-light w-100 me-2"
                        onClick={(e: any) => {
                          e.preventDefault();
                          setNotificationDropdownOpen(false);
                        }}
                      >
                        Close
                      </Link>
                      {/* <Link
                        to={routes.activity}
                        className="btn btn-primary w-100"
                      >
                        View All
                      </Link> */}
                    </div>
                  </div>
                </div>

                <div className="dropdown profile-dropdown">
                  <Link
                    to="#"
                    onClick={(e: any) => e.preventDefault()}
                    className="dropdown-toggle d-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <span className="avatar avatar-sm online">
                      <ImageWithBasePath
                        src={userImage || "assets/img/profiles/avatar-12.jpg"}
                        alt="Img"
                        className="img-fluid rounded-circle"
                      />
                    </span>
                  </Link>
                  <div className="dropdown-menu shadow-none">
                    <div className="card mb-0">
                      <div className="card-header">
                        <div className="d-flex align-items-center position-relative">
                          <div
                            className="avatar-wrapper position-relative me-2"
                            style={{ width: "60px", height: "60px" }}
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                          >
                            <ImageWithBasePath
                              src={
                                userImage || "assets/img/profiles/avatar-12.jpg"
                              }
                              alt="img"
                              className="avatar avatar-lg avatar-rounded"
                            />
                            {hover && (
                              <div
                                className="position-absolute top-0 end-0 bg-white rounded-circle p-1"
                                style={{ cursor: "pointer" }}
                                onClick={() => fileInputRef.current!.click()}
                              >
                                <i className="ti ti-pencil fs-9 text-primary"></i>
                              </div>
                            )}
                            <input
                              type="file"
                              ref={fileInputRef}
                              style={{ display: "none" }}
                              accept="image/*"
                              onChange={handleImageUpload}
                            />
                          </div>

                          <div>
                            <h5 className="mb-0">
                              {user?.userDetails.fname +
                                " " +
                                user?.userDetails.lname}
                            </h5>
                            <p className="fs-12 fw-medium mb-0">
                              {user?.userDetails.email}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* <div className="card-body">
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={routes.profile}
                        >
                          <i className="ti ti-user-circle me-1"></i>My Profile
                        </Link>
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={routes.bussinessSettings}
                        >
                          <i className="ti ti-settings me-1"></i>Settings
                        </Link>
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={routes.securitysettings}
                        >
                          <i className="ti ti-status-change me-1"></i>Status
                        </Link>
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={routes.profilesettings}
                        >
                          <i className="ti ti-circle-arrow-up me-1"></i>My
                          Account
                        </Link>
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={routes.knowledgebase}
                        >
                          <i className="ti ti-question-mark me-1"></i>Knowledge
                          Base
                        </Link>
                      </div> */}
                      <div className="card-footer">
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={`${all_routes.employeedetails.replace(
                            ":id",
                            user?.userDetails?.id
                          )}${location?.search}`}
                          state={{
                            type: "dashboard",
                          }}
                        >
                          <i className="ti ti-user me-2"></i>Profile
                        </Link>
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={all_routes.orginationChart}
                        >
                          <i className="ti ti-messages me-2"></i>
                          Organization Chat
                        </Link>

                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to="#"
                          onClick={(e: any) => {
                            e.preventDefault();
                            setShowChangePassword(true);
                          }}
                        >
                          <i className="ti ti-key me-2"></i>Change Password
                        </Link>
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={routes.login}
                          onClick={(e: any) => {
                            e.preventDefault();
                            handleLogout();
                          }}
                        >
                          <i className="ti ti-login me-2"></i>Logout
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showChangePassword && (
            <div className="modal-content p-4 bg-white rounded">
              <button
                className="btn-close float-end"
                onClick={() => setShowChangePassword(false)}
              ></button>
              <ChangePassword />
            </div>
          )}

          <div className="dropdown mobile-user-menu">
            <Link
              to="#"
              onClick={(e: any) => {
                e.preventDefault();
              }}
              className="nav-link dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fa fa-ellipsis-v"></i>
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              <Link
                className="dropdown-item"
                to={`${all_routes.employeedetails.replace(
                  ":id",
                  user?.userDetails?.id
                )}${location?.search}`}
                state={{
                  type: "dashboard",
                }}>
                My Profile
              </Link>
              <Link
                className="dropdown-item"
                to="#"
                onClick={(e: any) => e.preventDefault()}
              // to={`${routes.profilesettings}${location?.search}`}
              >
                Settings
              </Link>
              <Link
                className="dropdown-item"
                to={`${routes.login}${location?.search}`}
              >
                Logout
              </Link>


            </div>
          </div>
        </div>
      </div>
      {/* /Header */}
    </>
  );
};

export default Header;
